/**
 * Automatically validate the form on  submit, blur, keyup
 * Check for all options the docs
 *
 * Alternative for Abide or Parsley
 *
 * @author Michel Heitbrink
 */
import Plugin from './core/Plugin';
import Watcher from './validator/Watcher';
import Nutshell from './Nutshell';

class Validator extends Plugin  {

    static pluginName = 'Validator';

    static getDefaults() {

		return {
            selector: 'form[data-form-validate]',
            trigger: 'blur',
            message: {
                show: true,
                parent: true
            },
            classes: {
                message: {
                    show: 'c-form__error--show',
                    invalid: 'c-form__error-message'
                },
                container: {
                    valid: 'is-valid',
                    invalid: 'is-invalid'
                },
                input: {
                    valid: 'is-valid',
                    invalid: 'is-invalid'
                }
            },
            custom: {
                validators: {},
                patterns: {
                    password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&\#])[A-Za-z\d@$!%*?&\#]{8,}$/
                }
            },
            labels: {
                error: {
                    default: 'Deze waarde lijkt onjuist.',
                    required: 'Dit veld is verplicht.',
                    email: 'Dit lijkt geen geldig e-mail adres te zijn.',
                    url: 'Dit lijkt geen geldige URL te zijn.',
                    number: 'Deze waarde moet een nummer zijn.',
                    min: 'Deze waarde mag niet lager zijn dan %s.',
                    max: 'Deze waarde mag niet groter zijn dan %s.',
                    minlength: 'Deze tekst is te kort. Deze moet uit minimaal %s karakters bestaan.',
                    maxlength: 'Deze waarde is te lang. Deze mag maximaal %s karakters lang zijn.',
                    equalto: 'Deze waardes moeten identiek zijn.',
                    multiple: 'Meerdere waardes zijn verplicht. Selecteer minimaal %s waardes.'
                }
            }
		}
	}

    static getName() {
        return Validator.pluginName;
    }

    /**
     * Add the validator to the forms based on given selector
     *
     * @param  {object} options
     * @return {Array}
     */
    static assign(options) {

        let list = [];

        options = options || {};
        options = { ...Nutshell.options[Validator.pluginName], ...options };

        document.querySelectorAll(options.selector).forEach( (form) => {

            let validator = new Validator(form, options);
            list.push({ validator, form });

        });

        return list;
    }

    constructor(form, options) {

        super(options);

        this.form = form;
        this.watcher = new Watcher(form, this.options);

        this.setReferences();
        this.events();
    }

    setReferences() {
        this.references = {
            input: {
                valid: null,
                invalid: null
            },
            form: {
                valid: null,
                invalid: null,
                reset: null
            }
        };
    }

    /**
     * Add events to the form
     */
    events() {
        this.form.addEventListener('validate.input.invalid', this.references.input.invalid = this.invalidInputEvent.bind(this) );
        this.form.addEventListener('validate.input.valid', this.references.input.valid =  this.validInputEvent.bind(this) );
        this.form.addEventListener('validate.form.invalid', this.references.form.invalid = this.invalidFormEvent.bind(this) );
        this.form.addEventListener('validate.form.valid', this.references.form.valid = this.validFormEvent.bind(this) );
        this.form.addEventListener('validate.form.reset', this.references.form.reset = this.resetFormEvent.bind(this) );
    }

    clearEvents() {
        this.form.removeEventListener('validate.input.invalid', this.references.input.invalid );
        this.form.removeEventListener('validate.input.valid', this.references.input.valid );
        this.form.removeEventListener('validate.form.invalid', this.references.form.invalid );
        this.form.removeEventListener('validate.form.valid', this.references.form.valid );
        this.form.removeEventListener('validate.form.reset', this.references.form.reset  );
    }

    /**
     * Add errors and clear valid messages
     */
    invalidInputEvent(e) {
        this.addError(e.input, e.errors);
        this.removeValid(e.input);
    }

    /**
     * Add valid messages and remove errors
     */
    validInputEvent(e) {
        this.removeError(e.input);
        this.addValid(e.input);
    }

    /**
     * Add errors and clear valid messages
     */
    invalidFormEvent(e) {
        this.trigger('invalid');
    }

    /**
     * Add valid messages and remove errors
     */
    validFormEvent(e) {
        this.trigger('valid');
    }

    /**
     * Remove errors and clear valid messages
     */
    resetFormEvent() {
        this.removeErrors();
        this.removeValids();
    }

    /**
     * Add classes and error message to the input
     *
     * @param {HTMLElement} input
     * @param {Array} errors
     */
    addError(input, errors) {

        let related = this.watcher.inputs.filter( i => i.name == input.name );
        this.removeError(input);

        related.forEach( (element) => {

            /**
             * Add classes to the input
             */
            if ( this.options.classes.input && this.options.classes.input.invalid ){
                element.classList.add(this.options.classes.input.invalid);
            }

            /**
             * Add classes to the parent
             */
            if ( this.options.classes.container && this.options.classes.container.invalid ){
                element.parentNode.classList.add(this.options.classes.container.invalid);
            }


            if ( this.options.message.show ){

                /**
                 * Open error container if given
                 */
                if ( element.hasAttribute('data-error-container') ) {

                    let containers = this.form.querySelectorAll(element.getAttribute('data-error-container'));

                    containers.forEach( (container) => {

                        if ( this.options.classes.message && this.options.classes.message.show ){
                            container.classList.add(this.options.classes.message.show);
                        }

                    });
                }

                /**
                 * Create a span with the error message
                 */
                else {
                    let span = document.createElement("span");

                    if ( this.options.classes.message && this.options.classes.message.invalid ){
                        span.classList.add(this.options.classes.message.invalid);
                    }

                    span.dataset.validationError = '';

                    /**
                     * Add custom error to the span
                     */
                    if ( element.hasAttribute('data-error-message') ) {
                        span.innerHTML = element.getAttribute('data-error-message');
                    }

                    /**
                     * Find error message correspondending with the current error
                     */
                    else if ( Array.isArray(errors) && errors.length ) {

                        let error = errors[0];

                        if ( this.options.labels.error.hasOwnProperty(error) ) {

                            let text = this.options.labels.error[error];

                            if ( error == 'min' || error == 'max' || error == 'minlength' || error == 'maxlength' ) {
                                text = text.replace('%s', element.getAttribute(error));
                            }
                            else if ( error == 'multiple' ) {
                                text = text.replace('%s', element.getAttribute('data-min-required'));
                            }

                            span.innerHTML = text;
                        }
                        else if ( this.options.labels.error.hasOwnProperty(element.type) ) {
                            span.innerHTML = this.options.labels.error[element.type];
                        }
                        else {
                            span.innerHTML = this.options.labels.error.default;
                        }
                    }

                    /**
                     * Add the error as sibling to the input or the parent container
                     */
                    let target = ( this.options.message.parent ) ? element.parentNode : element;
                    target.parentNode.insertBefore(span, target.nextSibling);
                }
            }
        });
    }

    /**
     * Add valid classes to the input and parent container
     *
     * @param {HTMLElement} input
     */
    addValid(input) {

        let related = this.watcher.inputs.filter( i => i.name == input.name );
        this.removeError(input);

        related.forEach( (element) => {

            if ( this.options.classes.input && this.options.classes.input.valid ){
                element.classList.add(this.options.classes.input.valid);
            }

            if ( this.options.classes.container && this.options.classes.container.valid ){
                element.parentNode.classList.add(this.options.classes.container.valid);
            }
        });
    }

    /**
     * Remove error classes of the input and the parent container.
     *
     * @param  {HTMLElement} input
     */
    removeError(input) {

        let related = this.watcher.inputs.filter( i => i.name == input.name );

        related.forEach( (element) => {

            if ( this.options.classes.input && this.options.classes.input.invalid ){
                element.classList.remove(this.options.classes.input.invalid);
            }

            if ( this.options.classes.container && this.options.classes.container.invalid ){
                element.parentNode.classList.remove(this.options.classes.container.invalid);
            }

            if ( this.options.message.show ){

                let target = ( this.options.message.parent ) ? element.parentNode : element;

                if ( target.nextSibling
                    && target.nextSibling instanceof HTMLElement
                    && target.nextSibling.hasAttribute('data-validation-error')  ) {

                    target.nextSibling.remove();
                }

                if ( element.hasAttribute('data-error-container') ) {

                    let containers = this.form.querySelectorAll(element.getAttribute('data-error-container'));

                    containers.forEach( (container) => {

                        if ( this.options.classes.message && this.options.classes.message.show ){
                            container.classList.remove(this.options.classes.message.show);
                        }

                    });
                }
            }
        });
    }

    /**
     * Remove valid classes of the input and the parent container.
     *
     * @param  {HTMLElement} input
     */
    removeValid(input) {

        let related = this.watcher.inputs.filter( i => i.name == input.name );

        related.forEach( (element) => {

            if ( this.options.classes.input && this.options.classes.input.valid ){
                element.classList.remove(this.options.classes.input.valid);
            }

            if ( this.options.classes.container && this.options.classes.container.valid ){
                element.parentNode.classList.remove(this.options.classes.container.valid);
            }

        });
    }

    /**
     * Remove invalid classes of all inputs
     *
     * @param  {HTMLElement} input
     */
    removeErrors() {

        this.watcher.inputs.forEach( (input) => {
            this.removeError(input);
        })
    }

    /**
     * Remove valid classes of all inputs
     *
     * @param  {HTMLElement} input
     */
    removeValids() {

        this.watcher.inputs.forEach( (input) => {
            this.removeValid(input);
        })
    }

    /**
     * Trigger a event on the form
     *
     * @param  {string} name
     */
    trigger(name) {
        let ev = new Event(name, {"bubbles":true, "cancelable":false});
        this.form.dispatchEvent(ev);
    }

    /**
     * Reset the form
     */
    reset() {
        this.form.reset();
    }

    /**
     * Update the form if the HTML is changed
     */
    update() {
        this.removeErrors();
        this.removeValids();

        this.watcher.update();
    }

    /**
     * Validate the form without triggering the errors
     *
     * @return {boolean}
     */
    validate() {
        return this.watcher.validate(true);
    }

    /**
     * Remove all event listeneres
     */
    destroy() {
        this.removeErrors();
        this.removeValids();

        this.clearEvents();
        this.setReferences();
        this.watcher.destroy();
    }
}

export default Validator;


/*
---
name: Validator
category: JavaScript
subcategory: Helpers
---

Examples
======
A validator for your form. Add the property `data-form-validate` to your form element and trigger Setup.init when the page is ready with loading.
The validator is added by default to your project. To disable the validator simple add the option `assign.validator = false` to  Setup.init.

##### Options:
- **selector** (String) - The selector the determine which form has the abide functionality. Default: form[data-form-validate]
- **trigger** (String) - Event on which the validation must trigger. Options: keyup, blur, submit. Default: blur
- **message** (Object) - Parameters used for the error message
    - **show** - (Boolean) - Add the error message to the input. Default: true
    - **parent** - (Boolean) - Add the error as a sibling next the parent container. Else the message will be added as a sibling next to the input. Default: true
- **classes** (Object) - Parameters will be posted as formdata or querystring based on request method
    - **message.invalid** - (String) - Class added to the error message container. Default: `c-form__error-message`
    - **container.valid** - (String) - The class will be added to a valid input's container. Default `is-valid`
    - **container.invalid** - (String) - The class will be added to a invalid input's container. Default: `is-invalid`
    - **input.valid** - (String) - The class will be added to a valid input's container. By default `is-valid`
    - **input.invalid** - (String) - The class will be added to a invalid input's container. By default `is-invalid`
- **custom** (Object) - Object with custom validators and patterns. By default the pattern password is added.
    - **patterns** - (Object) - Custom patterns can be used to check the value. data-pattern='passsword' will trigger the pattern. Only add regexes.
    - **validators** - (Object) - Custom validators can be used to check the value. data-validator='passsword' will trigger the validator. Only use functions.
- **labels** (Object) - Object with all labels. Used by the validator. Can be overruled with a other language.

```js

import { Nutshell } from '@nutshell/core';

//Override the settings like this:
Nutshell.options.Validator.selector = 'form[data-form-validate]';

Setup.init();

const form = document.querySelector('#example-form');

if ( form ) {

    form.addEventListener('valid', (event) => {

        event.preventDefault();

        const rqst = new ServerRequest();

        rqst.submit('#example-form', 'method').then( (response) => {
            console.log(response);
        });
    });

    form.addEventListener('invalid', (event) => {
        console.error('invalid form');
    });
}

```

Also the inputs will receive a event

```js

input.addEventListener('is-invalid', (event) => {
    console.error('invalid input');
});

input.addEventListener('is-valid', (event) => {
    console.error('valid input');
});

//programmatically add custom validator to the input
input.validator = () => { return false };
```

In some cases the validator must be initialized on a other moment. This example explains how:

```js
import { Validator } from '@nutshell/core';

const form = document.querySelector('#example-form');

if ( form ) {

    const validator = new Validator(form, {
        custom: {
            pattern: {
                example: /^[a-zA-Z]+$/
            },
            validators: {
                example: (input) => {
                    return true;
                }
            }
        }
    });

    form.addEventListener('valid', (event) => {
        //Do something
    });

    form.addEventListener('invalid', (event) => {
        //Do something
    });

    //check the valid status without triggering the error messages
    const valid = validator.validate();

    //update the validator with new inputs (if the html has been changed)
    validator.update();

    //destroy the validator
    validator.destroy();
}
```
HTML Examples
```html
<div class='c-form-element'>
    <label for='required'>Required</label>
    <input type='text' id='required' name="required" required/>
</div>

<!-- Field must be equal to -->
<div class='c-form-element'>
    <label for='required'>Confirm Required</label>
    <input type='text' id='required-equelto' data-equalto="required" name="equelto" required/>
</div>

<!-- Type validation -->
<div class='c-form-element'>
    <label for='email'>E-mail</label>
    <input type='email' id='email' name="email" required/>
</div>

<!-- Example patterns -->
<div class='c-form-element'>
    <label for='password'>New password</label>
    <input type='password' name="password" required data-pattern="password" id='password'/>
</div>

<div class='c-form-element'>
    <label for='regex'>Regex</label>
    <input type='text' id='regex' data-pattern="^[a-z]+$" name="regex"/>
</div>

<!-- Example validators -->
<div class='c-form-element'>
    <label for='valdiator'>Validator</label>
    <input type='text' name="valdiator" required data-validator="example" id='valdiator'/>
</div>

<!-- Ignore field -->
<div class='c-form-element'>
    <label for='ignore'>Ignore</label>
    <input type='text' name="ignore" required data-ignore id='ignore'/>
</div>

<!-- Silent validation field -->
<div class='c-form-element'>
    <label for='ignore'>Silent</label>
    <input type='text' name="silent" required data-silent id='silent'/>
</div>

<!-- Minimum selected checkboxes -->
<div class="c-form-checkbox">
    <input data-min-required="2" type="checkbox" required name="checkbox-required-example" id="checkbox-required-example-1" />
    <label for="checkbox-required-example-1">This checkbox1 is required</label>
</div>

<div class="c-form-checkbox">
    <input data-min-required="2" type="checkbox" required name="checkbox-required-example" id="checkbox-required-example-2" />
    <label for="checkbox-required-example-2">This checkbox2 is required</label>
</div>

<!-- Display custom error container -->
<div class='c-form-element'>
   <label for='error-container'>Error Container</label>
   <input type='text' name="error-container" data-error-container="#error" data-pattern="^[a-z]+$" required id='error-container'/>
</div>

<div class="c-form__error" id="error">
	<h1>Example Error</h1>
</div>

```
*/
